/**
 * Main navigation bar that appears at the top of the page.
 * Used to navigate between primary pages and handle auth actions.
 */

import * as React from 'react';
import { ReactComponent as OptimicitiLogo } from '../../assets/img/brand/optimiciti_teal_clear_logo_only.svg';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ApiIcon from '@mui/icons-material/Api';
import BuildIcon from '@mui/icons-material/Build';
import CalculateIcon from '@mui/icons-material/Calculate';
import Dashboard from '@mui/icons-material/Dashboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';

import { MOBILE_NAV_BREAKPOINT } from 'utils/breakpoints';
import PolylineIcon from '@mui/icons-material/Polyline';
import RouteIcon from '@mui/icons-material/Route';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SummarizeIcon from '@mui/icons-material/Summarize';

import { SignedIn, SignedOut, UserButton, useClerk } from '@clerk/clerk-react';

import HoverMenu from 'material-ui-popup-state/HoverMenu';
import { bindHover, bindMenu, usePopupState } from 'material-ui-popup-state/hooks';

import { SettingsDialog } from 'components/common/dialogs';
import LoginSync from 'components/utilities/LoginSync.js';
import { selectSidebarMini } from 'store/reducers/uiReducer';


const ModelPopupMenu = ({ active, pages }) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'modelsMenu',
  });

  return (
    <React.Fragment>
      <Button
        {...bindHover(popupState)}
        sx={{
          color: active || popupState.isOpen ? 'primary.main' : 'dark.main',
          backgroundColor: active || popupState.isOpen ? 'rgba(0, 146, 139, 0.10)' : 'transparent',
          mx: 1,
          px: 2,
          flexShrink: 0,
          borderRadius: '8px',
          '&:hover': {
            color: 'primary.main',
            backgroundColor: active ? 'rgba(0, 146, 139, 0.10)' : 'rgba(0, 146, 139, 0.05)',
          },
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s',
        }}
      >
        <Box component="span" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
          Select a Model
          <ExpandMoreIcon sx={{ fontSize: '1.1rem', transition: 'transform 0.2s', transform: popupState.isOpen ? 'rotate(180deg)' : 'none' }} />
        </Box>
      </Button>
      <HoverMenu
        {...bindMenu(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        disableScrollLock
        PaperProps={{
          sx: {
            mt: 0, // Removed gap to prevent menu from closing when moving mouse from button to menu
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            borderRadius: 2,
            minWidth: 340,
            maxWidth: 420,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.25,
              whiteSpace: 'normal',
              '&:hover': {
                backgroundColor: 'rgba(0, 146, 139, 0.08)',
                '& .title': {
                  color: 'primary.main',
                },
              },
            },
          }
        }}
      >
        {pages.find(p => p.title === 'Models').subpages.map((sub) => (
          <MenuItem key={sub.title} component={Link} to={sub.link} onClick={popupState.close}>
            <Typography className="title" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', transition: 'color 0.2s' }}>
              {sub.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.3, whiteSpace: 'normal', fontSize: '0.8125rem' }}>
              {sub.description}
            </Typography>
          </MenuItem>
        ))}
      </HoverMenu>
    </React.Fragment>
  );
};

const MainNavBar = () => {
  const clerk = useClerk();
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_NAV_BREAKPOINT));
  const sidebarMini = useSelector(selectSidebarMini);

  const navPages = [
    {
      title: 'Home',
      link: '/home',
      icon: <HomeIcon />,
      description: 'Multi-stop route planning built for real fleet constraints.'
    },
    {
      title: 'Models',
      icon: <CalculateIcon />,
      subpages: [
        {
          title: 'Model Builder',
          link: '/model-builder',
          icon: <BuildIcon />,
          description: 'Start from your own stops, vehicles and constraints rather than an example.'
        },
        {
          title: 'Vehicle Routing',
          link: '/models/vehicle-routing',
          icon: <PolylineIcon />,
          description: 'Optimize routes for multiple vehicles with constraints like capacity, time windows, and driver hours.'
        },
        {
          title: 'Pickup & Dropoff',
          link: '/models/pickup-dropoff',
          icon: <RouteIcon />,
          description: 'Work that comes in pairs — collected at one place, delivered to another, pickup first.'
        },
      ]
    },
    {
      title: 'Examples',
      link: '/use-cases',
      icon: <SummarizeIcon />,
      description: 'Real scenarios you can open, change, and run.'
    },
    {
      title: 'Industries',
      link: '/industries',
      icon: <StorefrontIcon />,
      description: 'Nine industries, each with a worked example you can open and change.'
    },
    {
      title: 'Pricing',
      link: '/pricing',
      icon: <LocalOfferIcon />,
      description: 'What the free plan includes, and what Professional adds.'
    },
    {
      title: 'API',
      link: '/api',
      icon: <ApiIcon />,
      description: 'Solve from your own code — submit a problem, poll for the result.'
    },
  ];

  const pathname = location.pathname;
  const isHome = pathname === '/home' || pathname === '/index' || pathname === '/';
  // Keep the navbar consistent on the Home page and the rest of the app.
  // Home defaults to a collapsed sidebar, but we still show the same compact logo.
  const showCircleLogo = isHome || !sidebarMini;

  const isModelsPage =
    pathname.startsWith('/model-builder') ||
    pathname.startsWith('/models') ||
    pathname.startsWith('/examples');
  const isExamplesPage = pathname.startsWith('/use-cases');
  const isIndustriesPage = pathname.startsWith('/industries');
  const isPricingPage = pathname.startsWith('/pricing');
  const isApiPage = pathname.startsWith('/api');

  const [anchorElNav, setAnchorElNav] = React.useState(null);
  /* Settings is a dialog, not a route, and the sidebar was its only trigger
     anywhere in the app. The sidebar is desktop-only, so without this the
     Settings hub (units, optimization, coordinate system, time) would simply
     not exist on a phone. */
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const navButtonSx = (active) => ({
    color: active ? 'primary.main' : 'dark.main',
    mx: 0.5,
    px: 1,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    borderRadius: '8px',
    transition: 'all 0.2s',
    backgroundColor: active ? 'rgba(0, 146, 139, 0.10)' : 'transparent',
    '&:hover': active
      ? {
          // No hover-responsive styling when this is the current page.
          color: 'primary.main',
          backgroundColor: 'rgba(0, 146, 139, 0.10)',
        }
      : {
          color: 'primary.main',
          backgroundColor: 'rgba(0, 146, 139, 0.05)',
        },
  });

  return (
    <>
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <LoginSync />
      <Container maxWidth={false}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 48, md: 52 } }}>
          <Box 
            component={Link} 
            to="/home" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              mr: 4
            }}
          >
            <OptimicitiLogo
              style={{
                height: '2rem',
                width: showCircleLogo ? '2rem' : 'auto',
                transition: 'all 0.2s',
                maxHeight: 'none'
              }}
              aria-label="Optimiciti Logo"
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
              <Button component={Link} to="/home" sx={navButtonSx(isHome)}>
                Home
              </Button>
              <ModelPopupMenu active={isModelsPage} pages={navPages} />
              <Button component={Link} to="/use-cases" sx={navButtonSx(isExamplesPage)}>
                Examples
              </Button>
              <Button component={Link} to="/industries" sx={navButtonSx(isIndustriesPage)}>
                Industries
              </Button>
              <Button component={Link} to="/pricing" sx={navButtonSx(isPricingPage)}>
                Pricing
              </Button>
              <Button component={Link} to="/api" sx={navButtonSx(isApiPage)}>
                API
              </Button>
            </Box>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex' }}>
              <Tooltip title="Menu" placement="right">
                <IconButton
                  size="large"
                  aria-label="menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  sx={{ color: 'dark.main' }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                PaperProps={{
                  /* This menu is the whole navigation on a phone now, so it has
                     to fit one. minWidth alone let it run past the right edge
                     and the descriptions were cut off mid-word; MenuItem also
                     defaults to white-space: nowrap, so they need explicit
                     permission to wrap rather than overflow. */
                  sx: {
                    mt: 1.5,
                    minWidth: 240,
                    maxWidth: 'calc(100vw - 32px)',
                    borderRadius: 2,
                    '& .MuiMenuItem-root': { whiteSpace: 'normal' },
                    '& .MuiListItemText-root': { whiteSpace: 'normal' },
                  }
                }}
              >
                {navPages.map((page) => (
                  <Box key={page.title}>
                        {!page.subpages ? (
                      <MenuItem
                        component={Link}
                        to={page.link}
                        onClick={handleCloseNavMenu}
                        selected={
                          (page.link === '/home' && isHome) ||
                          (page.link === '/use-cases' && isExamplesPage) ||
                          (page.link === '/industries' && isIndustriesPage) ||
                          (page.link === '/pricing' && isPricingPage) ||
                          (page.link === '/api' && isApiPage)
                        }
                        sx={() => {
                          const isSelected =
                            (page.link === '/home' && isHome) ||
                            (page.link === '/use-cases' && isExamplesPage) ||
                            (page.link === '/industries' && isIndustriesPage) ||
                            (page.link === '/pricing' && isPricingPage) ||
                            (page.link === '/api' && isApiPage);

                          return {
                            py: 1.5,
                            ...(isSelected
                              ? {
                                  color: 'primary.main',
                                  backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                  },
                                }
                              : {
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                  },
                                }),
                          };
                        }}
                      >
                        <ListItemIcon sx={{ color: 'primary.main' }}>
                          {React.cloneElement(page.icon, { fontSize: 'small' })}
                        </ListItemIcon>
                        <ListItemText
                          primary={page.title}
                          secondary={page.description}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </MenuItem>
                    ) : (
                      <React.Fragment>
                        <MenuItem disabled sx={{ opacity: '1 !important', py: 1 }}>
                          <ListItemIcon sx={{ color: 'primary.main' }}>
                            {React.cloneElement(page.icon, { fontSize: 'small' })}
                          </ListItemIcon>
                          <ListItemText primary={page.title} primaryTypographyProps={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }} />
                        </MenuItem>
                        {page.subpages.map((sub) => (
                          <MenuItem
                            key={sub.title}
                            component={Link}
                            to={sub.link}
                            onClick={handleCloseNavMenu}
                            selected={pathname === sub.link || pathname.startsWith(`${sub.link}/`)}
                            sx={() => {
                              const isSelected = pathname === sub.link || pathname.startsWith(`${sub.link}/`);
                              return {
                                pl: 4,
                                py: 1.2,
                                ...(isSelected
                                  ? {
                                      color: 'primary.main',
                                      backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                      },
                                      '&.Mui-selected:hover': {
                                        backgroundColor: 'rgba(0, 146, 139, 0.10)',
                                      },
                                    }
                                  : null),
                              };
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {React.cloneElement(sub.icon, { fontSize: 'small' })}
                            </ListItemIcon>
                            <ListItemText
                              primary={sub.title}
                              secondary={sub.description}
                              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                          </MenuItem>
                        ))}
                      </React.Fragment>
                    )}
                  </Box>
                ))}
                {/* The sidebar's tail end, which has no other home on a phone.
                    Account is a link there whether or not you are signed in, so
                    it is one here too — the Clerk avatar menu also reaches it,
                    but only once Clerk has loaded and only when signed in. */}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  component={Link}
                  to="/account"
                  onClick={handleCloseNavMenu}
                  selected={pathname.startsWith('/account')}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Account"
                    secondary="Your runs, saved scenarios and plan."
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCloseNavMenu();
                    setSettingsOpen(true);
                  }}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Settings"
                    secondary="Units, optimization, coordinates and time."
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          )}

          <Box sx={{ flexGrow: 0, ml: 2 }}>
            <SignedOut>
              <Button
                variant="contained"
                onClick={() => clerk.openSignIn({ mode: 'modal' })}
                sx={{
                  color: '#fff',
                  backgroundColor: 'primary.main',
                  borderColor: 'primary.main',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'primary.extraDark',
                    backgroundColor: 'primary.extraDark'
                  }
                }}
              >
                Login
              </Button>
            </SignedOut>
            <SignedIn>
              <UserButton appearance={{ elements: { userButtonAvatarBox: { width: 40, height: 40 } } }}>
                <UserButton.MenuItems>
                  <UserButton.Link 
                    label="Account Dashboard" 
                    href="/account" 
                    labelIcon={<Dashboard sx={{ fontSize: 18 }} />} 
                  />
                </UserButton.MenuItems>
              </UserButton>
            </SignedIn>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
    {/* Portals to <body> and renders nothing while closed, so it adds no
        layout of its own next to the sticky AppBar. */}
    <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

export default MainNavBar;
